/* 
-----------------------------------------------------------------------------
| PERSONAL DETAILS PAGE
-----------------------------------------------------------------------------
- description: no booking data logic goes here, just metrics, redirects etc.
*/
import { FC, useEffect, useRef, useState } from "react";
import { useApp } from "../context/useApp";
import { DEFAULT_DIALOG_HANDLE, LANG, ROUTES, rumba, VENUE_ID } from "../utils/symbols";
import { PersonalDetailsForm } from "../components/personal-details/PersonalDetailsForm";
import { Aside } from "../components/personal-details/Aside";
import { Appearance, loadStripe } from "@stripe/stripe-js";
import { loadEnvValue } from "../utils/helpers";
import { _ } from "../utils/helpers";
import { useDefaultDialogs } from "../hooks/useDefaultDialogs";
import { AsideInsideElements } from "../components/personal-details/AsideInsideElements";
import { StepControls } from "../components/table-layout/aside/StepControls";
import { Elements } from "@stripe/react-stripe-js";
import { HelpButton } from "../parts/HelpButton";

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
let stripePromise = loadStripe(loadEnvValue('REACT_APP_STRIPE_LOAD'), { locale: LANG } as any);

const stripeAppearance: Appearance = {
    variables: {
        borderRadius: VENUE_ID === 1 ? "6px" : "0px",
        colorBackground: "#fff",
        focusOutline: "none",
        focusBoxShadow: "none",
        spacingUnit: "5px",
        colorDanger: "#E5240B",
    },
    theme: "flat",
    rules: {
        '.Button': {
            fontSize: "20px",
        },
        '.Input': {
            border: '2px solid #1D1E1F',
            padding: '13px 16px',   // 👈 clave
            fontSize: '16px',
            lineHeight: '20px',
        },
        '.Input:focus': {
            border: '2px solid #00AD88',
        },
        '.Input--empty': {
            border: '2px solid #C0C2C3',
        },
        '.Input--invalid': {
            border: '2px solid #E5240B',
            outline: 'none',
            boxShadow: "none",
        },
        '.Input--invalid:focus': {
            border: '2px solid #E5240B',
            outline: 'none',
            boxShadow: "none",
        },
    }
}

export const PersonalDetailsPage: FC = () => {

    const {
        state: { booking, dialogHandles, forcedRenders, shifts },
        actions: { navigate, genericError }
    } = useApp()

    const journey = booking.journey

    const [options, setOptions] = useState<{ clientSecret: string } | null>(null)
    const [stripeComplete, setStripeComplete] = useState<boolean>(false)
    const [stripeError, setStripeError] = useState<boolean>(false)

    const defaultDialogs = useDefaultDialogs()
    const firstTime = useRef(true)
    const dialogHandle = dialogHandles.get('app-dialog') ?? DEFAULT_DIALOG_HANDLE

    const getClientSecret = async () => {

        firstTime.current = false

        dialogHandle.openDialog({ dialog: 'loading', className: 'w-100 df aic jcc loading' })
        const bookingSummary = booking.getBookingSummary()
        const amount = Math.floor(bookingSummary.total * 100)

        const response = await rumba.getClientSecret(amount, booking)
        if (response.result !== 'ok') {
            defaultDialogs.show('no-tables')
            return
        }
        if (response.result === 'ok' && response.amount !== amount) {
            genericError(() => {
                navigate(ROUTES.TABLE_LAYOUT)
                booking.setBookingStep('products').render()
            })
            return
        }

        setOptions({ clientSecret: response.intent })
        dialogHandle.closeDialog()
    }



    useEffect(() => {

        if (dialogHandles.size === 0) return

        if (booking.step === 'date') { navigate(ROUTES.DATE); return }
        if (booking.step === 'shift') { navigate(ROUTES.TABLE_LAYOUT); return }
        if (booking.step === 'tickets') { navigate(ROUTES.TABLE_LAYOUT); return }
        if (booking.step === 'products') { navigate(ROUTES.TABLE_LAYOUT); return }

        if (journey === 'B') return

        // Check if Stripe is loaded
        stripePromise.then(() => setStripeError(false)).catch((err) => {
            console.error("Stripe load error:", err);
            setStripeError(true);
        });

        return () => setOptions(null)

    }, [dialogHandles, journey])

    useEffect(() => {

        const shift = shifts.find((shift) => shift.id === booking.shiftId)
        if (!shift) return

        const errors = booking.validatePersonalDetail({ shift: shift })
        if (errors.length === 0 && firstTime.current && journey !== 'B') getClientSecret()

    }, [forcedRenders, shifts])

    const handleRetryStripe = () => {
        window.location.reload()
        // setStripeError(false);
        // stripePromise = loadStripe(loadEnvValue('REACT_APP_STRIPE_LOAD'), { locale: LANG } as any);
        // stripePromise.then(() => setStripeError(false)).catch(() => setStripeError(true));
    }

    if (stripeError && journey !== 'B') {
        return (
            <main className="page-container df aic jcc fd-c layout-b w-100">
                <div className="df aic jcc fd-c p-10 t-center">
                    <h2 className="text s24-600 mb-5">{_('0ge_anune__')}</h2>
                    <p className="text soft s16-400 mb-8 max-w-400">
                        {_('0pa_error_4')}
                    </p>
                    <button 
                        type="button"
                        onClick={handleRetryStripe}
                        className="button r3 px-10 py-5 text s18-500 action-2 b-0 pointer w-100"
                    >
                        {_('0ge_retry__')}
                    </button>
                    <HelpButton className="w-100 mt-7" />
                </div>
            </main>
        )
    }

    return options ? (
        <Elements stripe={stripePromise} options={{ ...options, appearance: stripeAppearance }}>
            <main
                id="personal-details-page"
                className="page-container df aic jcc fd-c layout-b w-100 test-ab-page"
                aria-label={_('3zo_tocon_i')}
            >
                <StepControls />
                <div className="h-100 w-100 df aifs jcsb px-10 zones layout-b-content">
                    <PersonalDetailsForm setStripeComplete={setStripeComplete} insideElements clientSecret={options?.clientSecret} journey={journey} />
                    <AsideInsideElements stripeComplete={stripeComplete} clientSecret={options?.clientSecret} />
                </div>
            </main>
        </Elements>
    )
        :
        <main
            id="personal-details-page"
            className="page-container df aic jcc fd-c layout-b w-100 test-ab-page"
            aria-label={_('3zo_tocon_i')}
        >
            <StepControls />
            <div className="h-100 w-100 df aifs jcsb px-10 zones layout-b-content">
                <PersonalDetailsForm setStripeComplete={setStripeComplete} journey={journey} />
                <Aside stripeComplete={stripeComplete} />
            </div>
        </main>
}
